import { useNavigate } from "react-router-dom";
import { PlayCircle, Link2, ShieldCheck, RefreshCcw, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { PRIMARY, Reveal, Kicker } from "@/components/landing/shared";

// One recorded end-to-end walkthrough. The .mp4 lives in /public and is served as a
// static asset (not import-resolved), so the build never depends on it being present.
const BEATS = [
  { i: Link2, t: "Connect once", b: "Run /runebox:login with your team key. Runebox detects every AI coding agent on the machine — Claude Code, Cursor, and the rest — and feeds them all from one catalog." },
  { i: ShieldCheck, t: "Publish, governed", b: "Upload or author a skill. Every submission is safety-scanned for malware and prompt-injection, then admin-approved before it can reach anyone." },
  { i: RefreshCcw, t: "Sync everywhere", b: "One /runebox:sync drops the approved catalog onto every engineer's machine — and yanking it upstream removes it everywhere on the next sync." },
];

export default function SeeItInAction() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const startTeam = () => navigate(user ? "/orgs/new" : "/signup?redirect=/orgs/new");
  const openGallery = () => navigate("/marketplace");
  const goLogin = () => navigate("/login");

  return (
    <div className="min-h-screen bg-[color:var(--rb-void)]">
      <LandingNav user={user} login={goLogin} startTeam={startTeam} navigate={navigate} />

      <main id="top" data-testid="see-it-in-action">
        <section className="rb-grain relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(680px circle at 50% -6%, rgba(45,212,191,0.12), transparent 55%)" }} />
          <div className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:py-20">
            <Reveal>
              <div className="text-center">
                <Kicker><PlayCircle className="h-3.5 w-3.5" /> See it in action</Kicker>
                <h1 className="rb-display mx-auto max-w-3xl text-4xl font-semibold leading-[1.08] tracking-tight text-[color:var(--rb-text)] sm:text-5xl">
                  From an empty machine to a synced team &mdash; <span className="text-[color:var(--rb-accent)]">in one walkthrough.</span>
                </h1>
                <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[color:var(--rb-text-dim)]">
                  Watch a skill go from upload, through a safety scan and approval, out to Claude Code and Cursor on a developer's machine &mdash; then get pulled back the moment it's yanked.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-10 overflow-hidden rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] shadow-[0_24px_70px_rgba(0,0,0,0.5)]">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster="/runebox-demo-poster.jpg"
                  data-testid="see-it-in-action-video"
                  className="block w-full"
                  style={{ aspectRatio: "2036 / 1080", background: "var(--rb-void)" }}
                >
                  <source src="/runebox-demo.mp4" type="video/mp4" />
                  Your browser can&rsquo;t play this video.{" "}
                  <a href="/runebox-demo.mp4" className="text-[color:var(--rb-accent)] underline">Download it</a> instead.
                </video>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button onClick={startTeam} data-testid="demo-primary-cta" className={`h-12 rounded-xl px-7 text-base ${PRIMARY}`}>
                  Start a team registry <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
                <p className="text-sm text-[color:var(--rb-text-faint)]">Free for up to 5 seats &mdash; no credit card required.</p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-4 lg:py-8">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {BEATS.map(({ i: Icon, t, b }, idx) => (
                <Reveal key={t} delay={idx * 0.06}>
                  <div className="h-full rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-6">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)]">
                      <Icon className="h-5 w-5 text-[color:var(--rb-accent)]" />
                    </div>
                    <h3 className="rb-display mt-4 text-lg font-semibold text-[color:var(--rb-text)]">{t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">{b}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <FinalCTA startTeam={startTeam} openGallery={openGallery} />
      </main>

      <LandingFooter login={goLogin} />
    </div>
  );
}
